import { useEffect, useState } from 'react';
import { helpdeskSettingsApi } from '../../api/services/helpdesk-settings.service';
import type { TicketStatusDef } from '../../types/helpdeskSettingsInterfaces';
import {
  TICKET_STATUS_LABELS,
  TICKET_STATUS_COLORS,
} from '../../types/ticketInterfaces';

/*
  Словарь статусов заявок из справочника бэка (/helpdesk/ticket-statuses/).

  Модульный кэш: страницы тикетов (список, карточка) используют один и
  тот же результат без повторных запросов и без редакс-обвязки — компонентам
  вне settings-контекста словарь нужен только для отображения.
  До прихода данных (или при ошибке) работают fallback-карты трёх
  системных статусов из ticketInterfaces.
*/

let cache: TicketStatusDef[] | null = null;
let pending: Promise<TicketStatusDef[]> | null = null;

export function loadTicketStatuses(): Promise<TicketStatusDef[]> {
  if (cache) return Promise.resolve(cache);
  if (pending) return pending;
  const request = helpdeskSettingsApi
    .getTicketStatuses()
    .then((res) => {
      cache = res.data;
      return res.data;
    })
    .catch(() => {
      // Не кэшируем ошибку: следующий вызов попробует снова.
      pending = null;
      return [] as TicketStatusDef[];
    });
  pending = request;
  return request;
}

export interface TicketStatusDict {
  statuses: TicketStatusDef[];
  labelFor: (code: string) => string;
  colorFor: (code: string) => string;
  /** Активные статусы для селектов/фильтров, отсортированы по sort_order. */
  options: { value: string; label: string }[];
}

function buildDict(statuses: TicketStatusDef[]): TicketStatusDict {
  const byCode = new Map(statuses.map((s) => [s.code, s]));
  return {
    statuses,
    labelFor: (code) =>
      byCode.get(code)?.name ?? TICKET_STATUS_LABELS[code] ?? code,
    colorFor: (code) =>
      byCode.get(code)?.color ?? TICKET_STATUS_COLORS[code] ?? '#9E9E9E',
    options: statuses.length
      ? statuses
          .filter((s) => s.is_active)
          .sort((a, b) => a.sort_order - b.sort_order)
          .map((s) => ({ value: s.code, label: s.name }))
      : Object.entries(TICKET_STATUS_LABELS).map(([value, label]) => ({
          value,
          label,
        })),
  };
}

export function useTicketStatusDict(): TicketStatusDict {
  const [statuses, setStatuses] = useState<TicketStatusDef[]>(cache ?? []);

  useEffect(() => {
    let cancelled = false;
    if (!cache) {
      loadTicketStatuses().then((data) => {
        if (!cancelled) setStatuses(data);
      });
    }
    return () => {
      cancelled = true;
    };
  }, []);

  return buildDict(statuses);
}
